import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import VideoCard from '@/components/feature/VideoCard';
import { articles } from '@/mocks/articles';
import { videos } from '@/mocks/videos';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => {
    const video = videos.find((v) => v.slug === slug);
    return video ? a.videoId === video.id : false;
  });

  const video = videos.find((v) => v.slug === slug);

  if (!article || !video) {
    return (
      <PageLayout showBreadcrumb={false}>
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">视频未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要查看的视频不存在或已下架。</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-xl transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const relatedVids = videos.filter(
    (v) => article.relatedVideos.includes(v.id) && v.id !== video.id
  ).slice(0, 3);

  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    description: video.description,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '向日葵视频',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.xiangrikuishipin.com/' },
      { '@type': 'ListItem', position: 2, name: '视频详情', item: `https://www.xiangrikuishipin.com/article/${slug}` },
    ],
  };

  return (
    <PageLayout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <section className="w-full px-4 md:px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <article className="flex-1 min-w-0">
              {/* Video Thumbnail/Player Area */}
              <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-6 bg-background-900">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 cursor-pointer transition-colors">
                    <i className="ri-play-fill text-3xl md:text-4xl text-background-50 ml-1"></i>
                  </span>
                </div>
                <span className="absolute bottom-3 right-3 bg-black/70 text-background-50 text-sm px-3 py-1 rounded-md font-medium">
                  {article.duration}
                </span>
              </div>

              {/* Article Header */}
              <header className="mb-6">
                <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-foreground-500">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-100 text-secondary-800 rounded-full text-xs font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {article.categoryName}
                  </Link>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {formatViews(article.views)} 次观看
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-primary-500"></i>
                    {article.rating} 分
                  </span>
                </div>
              </header>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Article Content */}
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_h3]:font-heading [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-semibold [&_h3]:text-foreground-800 [&_h3]:mt-6 [&_h3]:mb-3
                  [&_p]:mb-4 [&_p]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>

            {/* Sidebar */}
            <aside className="lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-20">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  相关推荐
                </h3>
                {relatedVids.length > 0 ? (
                  <div className="space-y-4">
                    {relatedVids.map((v) => (
                      <VideoCard key={v.id} video={v} showCategory />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {videos
                      .filter((v) => v.category === video.category && v.id !== video.id)
                      .slice(0, 3)
                      .map((v) => (
                        <VideoCard key={v.id} video={v} showCategory />
                      ))}
                  </div>
                )}
                <div className="mt-6 p-4 bg-primary-50/50 rounded-xl border border-primary-100">
                  <h4 className="font-heading text-foreground-900 font-semibold text-sm mb-2">
                    关于向日葵视频
                  </h4>
                  <p className="text-foreground-500 text-xs leading-relaxed mb-3">
                    向日葵视频提供91视频、97视频等数字视频平台的高清免费在线观看，覆盖51视频、久久视频等数字视频大全。
                  </p>
                  <Link
                    to="/about"
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                  >
                    了解更多 &rarr;
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}